<template>
  <van-popup :show="show" @update:show="$emit('update:show', $event)" :close-on-click-overlay="false"
    class="withdraw-password-dialog" round>
    <div class="withdraw-password-content">
      <!-- 关闭按钮 -->
      <div class="close-button" @click="handleClose">
        <img :src="getAssetsFile('activity/closepass.png')" alt="close" class="close-icon">
      </div>

      <!-- 标题 -->
      <div class="dialog-title">
        {{ t('withdrawPasswordDialog.title') }}
      </div>

      <!-- 提现信息 -->
      <div class="withdraw-info">
        <div class="withdraw-label">{{ t('withdrawPasswordDialog.withdrawFunds') }}</div>
        <div class="withdraw-amount">{{ VITE_symbol }}{{ numberFormat(amount) }}</div>
      </div>

      <!-- 分割线 -->
      <div class="divider"></div>

      <!-- 手续费信息 -->
      <!-- <div class="service-fee">
        <div class="fee-label">{{ t('withdrawPasswordDialog.serviceFee') }}</div>
        <div class="fee-amount">{{ VITE_symbol }}{{ numberFormat(serviceFee) }}</div>
      </div> -->

      <!-- 密码输入区域 -->
      <div class="password-input-container">
        <div class="flex mt-[15px] items-center justify-center justify-between" @click="focusFirstInput">
          <div v-for="(digit, index) in 6" :key="index" class="password-digit-box"
            :class="{ 'filled': index < password.length }">
            {{ password[index] || '' }}
            <!-- 光标 -->
            <div v-if="index === password.length && isFocused" class="password-cursor"></div>
          </div>
        </div>

        <!-- 隐藏的输入框用于接收键盘输入 -->
        <input ref="passwordInputRef" :value="password" type="tel" inputmode="numeric" pattern="[0-9]*"
          class="hidden-input" :maxlength="6" @input="onPasswordInput" @keydown="onKeydown" @focus="onFocus"
          @blur="onBlur" />
      </div>

      <!-- 错误提示 -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAssetsFile } from '@/utils/pub-use'
import { numberFormat } from '@/utils/formatter'
import * as GameApi from "@/api/game"
const VITE_symbol = import.meta.env.VITE_SYMBOL
const { t } = useI18n()

interface Props {
  show: boolean
  amount: number
  withdrawParams: any // 提现参数
}

interface Emits {
  (e: 'update:show', value: boolean): void
  (e: 'success'): void
  (e: 'error', message: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const password = ref('')
const passwordInputRef = ref<HTMLInputElement>()
const isFocused = ref(false)
const errorMessage = ref('')
const isProcessing = ref(false) // 防止在提现过程中继续输入

// 计算手续费（提现金额的1%）
const serviceFee = computed(() => {
  return Math.round(props.amount * 0.01 * 100) / 100
})

const handleClose = () => {
  emit('update:show', false)
  resetForm()
}

const resetForm = () => {
  password.value = ''
  errorMessage.value = ''
  isFocused.value = false
  isProcessing.value = false
  // 重置输入框值
  nextTick(() => {
    if (passwordInputRef.value) {
      passwordInputRef.value.value = ''
      // 强制清空输入框，防止残留值
      passwordInputRef.value.blur()
    }
  })
}

// 处理密码输入
const onPasswordInput = async (event: Event) => {
  // 如果正在处理中，忽略输入
  if (isProcessing.value) {
    return
  }

  const target = event.target as HTMLInputElement
  const inputValue = target.value

  // 只允许输入数字，并限制长度
  const numbersOnly = inputValue.replace(/\D/g, '')
  const newValue = numbersOnly.slice(0, 6)

  // 确保按顺序添加，防止某些安卓设备输入顺序错乱
  // 如果新值长度大于当前值，说明是新增输入，直接使用新值
  // 如果新值长度小于当前值，说明是删除，也直接使用新值
  // 如果长度相同但内容不同，可能是替换，需要检查是否是顺序添加
  if (newValue.length > password.value.length) {
    // 新增输入：确保是顺序添加（新值应该包含旧值作为前缀）
    if (newValue.startsWith(password.value)) {
      password.value = newValue
    } else {
      // 如果不是顺序添加，可能是输入法问题，强制按顺序
      password.value = password.value + numbersOnly.slice(password.value.length, password.value.length + 1)
    }
  } else {
    // 删除或替换操作，直接使用新值
    password.value = newValue
  }

  // 确保输入框的值与 password.value 同步，防止残留值
  nextTick(() => {
    if (passwordInputRef.value) {
      // 强制同步输入框的值
      passwordInputRef.value.value = password.value
      passwordInputRef.value.setSelectionRange(password.value.length, password.value.length)
    }
  })

  errorMessage.value = ''

  // 当输入6位数字时自动调用提现接口
  if (password.value.length === 6) {
    await handleWithdraw()
    // 注意：如果提现失败，isProcessing 会在 resetForm 中重置
  }
}

// 处理键盘事件
const onKeydown = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab']
  const isNumber = /^[0-9]$/.test(event.key)

  if (!isNumber && !allowedKeys.includes(event.key)) {
    event.preventDefault()
  }

  // 处理删除键，确保按顺序删除
  if (event.key === 'Backspace' || event.key === 'Delete') {
    nextTick(() => {
      if (passwordInputRef.value) {
        const currentValue = passwordInputRef.value.value.replace(/\D/g, '').slice(0, 6)
        password.value = currentValue
        passwordInputRef.value.setSelectionRange(password.value.length, password.value.length)
      }
    })
  }
}

// 聚焦事件
const onFocus = () => {
  isFocused.value = true
}

// 失焦事件
const onBlur = () => {
  isFocused.value = false
}

// 聚焦到输入框
const focusFirstInput = () => {
  nextTick(() => {
    if (passwordInputRef.value) {
      passwordInputRef.value.focus()
      // 确保光标在末尾
      passwordInputRef.value.setSelectionRange(password.value.length, password.value.length)
    }
  })
}

// 处理提现
const handleWithdraw = async () => {
  // 防止重复提交（如果已经在处理中，直接返回）
  if (isProcessing.value) {
    return
  }

  if (password.value.length !== 6) {
    errorMessage.value = t('withdrawPasswordDialog.pleaseEnter6Digits')
    return
  }

  // 设置处理中标志，防止重复提交
  isProcessing.value = true

  try {
    // 先调用 create 接口创建银行信息
    let bankAccountId

    // 新增：CPF 处理
    // if (props.withdrawParams.paymentType === 3) {
    //   // CPF
    //   console.log('调用 CPF create 接口，参数:', props.withdrawParams.cpfParams)
    //   const response = await GameApi.createbankInformation(props.withdrawParams.cpfParams)
    //   console.log('CPF create 接口响应:', response)
    //   if (response.code === 0) {
    //     bankAccountId = response.data
    //   } else {
    //     emit('error', response.msg || '创建CPF信息失败')
    //     emit('update:show', false)
    //     resetForm()
    //     return
    //   }
    // } else

    // 所有渠道类型都走相同逻辑：有银行卡就走提现，没有就走创建
    // 统一使用 bankParams，不再区分 paymentType
    if (props.withdrawParams.bankParams && props.withdrawParams.bankParams.bankAccountId) {
      // 如果已有 bankAccountId，直接使用（走提现接口）
      bankAccountId = props.withdrawParams.bankParams.bankAccountId
    } else {
      // 创建新的银行信息（走 create 接口）
      // 所有渠道都使用 bankParams
      const response = await GameApi.createbankInformation(props.withdrawParams.bankParams) as any
      if (response.code === 0) {
        bankAccountId = response.data
      } else {
        emit('error', response.msg)
        emit('update:show', false)
        resetForm()
        return
      }
    }

    // 构建提现参数，添加 withdrawPassword 字段
    const withdrawParams = {
      flbBankNameId: props.withdrawParams.bankParams.flbBankNameId,
      userId: props.withdrawParams.userId,
      withdrawAmount: props.withdrawParams.withdrawAmount,
      currency: props.withdrawParams.currency,
      bankAccountId: bankAccountId,
      paymentType: props.withdrawParams.paymentType,
      withdrawPassword: password.value,
      mobileNumber: props.withdrawParams.bankParams.mobileNumber,
      accountHolder: props.withdrawParams.bankParams.accountHolder,
      accountNumber: props.withdrawParams.bankParams.accountNumber,
    }

    // 调用提现接口
    const withdrawResponse = await GameApi.withdraw(withdrawParams) as any
    // 统一处理提现响应 - 检查响应结构：可能是 response.code 或 response.data.code
    const responseCode = withdrawResponse?.code ?? withdrawResponse?.data?.code
    const isSuccess = responseCode === 0 || responseCode === 200

    if (isSuccess) {
      emit('success')
      handleClose()
    } else {
      // 密码错误时，关闭弹窗并通知父组件，让用户重新点击提现按钮
      const errorMsg = withdrawResponse?.msg ?? withdrawResponse?.data?.msg ?? withdrawResponse?.message ?? t('withdrawPasswordDialog.passwordError')
      emit('error', errorMsg)
      emit('update:show', false)
      resetForm()
    }
  } catch (error: any) {
    // 捕获错误时，关闭弹窗并通知父组件
    const errorMsg = error?.message ?? error?.msg ?? t('withdrawPasswordDialog.withdrawFailed')
    emit('error', errorMsg)
    emit('update:show', false)
    resetForm()
  }
}

// 组件挂载后自动聚焦
onMounted(() => {
  if (props.show) {
    focusFirstInput()
  }
})

// 监听弹窗显示状态
watch(() => props.show, (newShow) => {
  if (newShow) {
    // 弹窗打开时，强制重置所有状态，防止上一次的输入残留
    resetForm()
    nextTick(() => {
      // 确保输入框完全清空
      if (passwordInputRef.value) {
        passwordInputRef.value.value = ''
      }
      focusFirstInput()
    })
  } else {
    // 弹窗关闭时也重置状态
    resetForm()
  }
})
</script>

<style scoped>
.withdraw-password-dialog {
  width: 85%;
  max-width: 320px;
  background: #085D63;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.withdraw-password-content {
  padding: 20px;
  text-align: center;
  position: relative;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
  z-index: 10;
}

.close-icon {
  width: 11px;
  height: 11px;
}

.dialog-title {
  color: #ffffff;
  font-size: 15px;
  padding-top: 20px;
  font-weight: bold;
  text-align: center;
}

.withdraw-info {
  margin-top: 15px;
}

.withdraw-label {
  color: #ffffff;
  font-size: 15px;
}

.withdraw-amount {
  color: #F5B44C;
  font-size: 18px;
  margin-top: 14px;
  font-weight: bold;
}

.divider {
  height: 1px;
  background: #1B8362;
  margin: 15px 0;
}

.service-fee {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.fee-label {
  color: #ffffff;
  font-size: 12px;
}

.fee-amount {
  color: #ffffff;
  font-size: 12px;
}

.password-input-container {
  margin-bottom: 20px;
  position: relative;
}

.password-digit-box {
  width: 44px;
  height: 44px;
  border: 2px solid #009F76;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 18px;
  font-weight: bold;
  position: relative;
  background: #00563B;
  transition: all 0.3s ease;
  margin: 0 4px;
}

.password-digit-box.filled {
  border-color: #13C769;
  background: rgba(19, 199, 105, 0.1);
}

.password-cursor {
  width: 2px;
  height: 20px;
  background: #ffffff;
  animation: blink 1s infinite;
  position: absolute;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}

.hidden-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-bottom: 15px;
  text-align: center;
}
</style>
